/**
 * The help assistant's chat panel (D10-UI-002..005, #3604).
 *
 * Built on the shared `<Modal>` (portal, focus trap, Esc-to-close, focus return
 * to the bubble, `.crm-overlay-enter`, bottom sheet on mobile) with its
 * "corner" placement: a floating panel docked bottom-right from `sm:` up.
 * Shows the streaming conversation, or offline help when the service cannot
 * chat. "Report a problem" opens the existing feedback form; "Take a tour"
 * starts this page's tour, else the user's welcome tour. The assistant's own
 * `start_tour` / `open_feedback` actions are carried out by `useSupportActions`
 * (D10-ACT, #3605). A voice note (#3829) is transcribed into the input at the
 * cursor for the user to review before sending -- never sent on its own.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { Bug, GraduationCap, Send, SquarePen } from "lucide-react";
import Modal from "../ui/Modal";
import { BeyondMark } from "../brand/BeyondMark";
import { useSupportChat } from "../../contexts/supportChat";
import { useTour } from "../../contexts/tour";
import { openFeedback } from "../../lib/feedback/openFeedback";
import { MAX_MESSAGE_CHARS } from "../../api/support";
import { OfflineHelp } from "./OfflineHelp";
import { SupportMessageList } from "./SupportMessageList";
import { SuggestedQuestions } from "./SuggestedQuestions";
import { useSupportActions } from "./useSupportActions";
import { useSupportLists } from "./useSupportLists";
import { VoiceNoteButton, VoiceNoteStatus } from "../voice/VoiceNoteButton";
import { insertAtCursor, useVoiceNote } from "../voice/useVoiceNote";

interface Props {
  /** Display name from the status endpoint (never hard-coded; white-label). */
  assistantName: string;
}

export function SupportPanel({ assistantName }: Props) {
  const chat = useSupportChat();
  const { startTour } = useTour();
  const location = useLocation();
  const { allowedRoutes, allowedTours, quickTourId } = useSupportLists();
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const greetingRef = useRef<HTMLDivElement>(null);
  useSupportActions();

  // Voice note (#3829): the transcript lands in the input at the cursor, unsent.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const voice = useVoiceNote((text) => {
    const el = inputRef.current;
    const next = insertAtCursor(el, draftRef.current, text, MAX_MESSAGE_CHARS);
    setDraft(next.value);
    window.setTimeout(() => {
      el?.focus();
      el?.setSelectionRange?.(next.caret, next.caret);
    }, 0);
  });

  // The panel hides rather than unmounts, so closing it abandons a voice note:
  // mic released, upload aborted, nothing inserted into the hidden input.
  const cancelVoice = voice.cancel;
  useEffect(() => {
    if (!chat.isOpen) cancelVoice();
  }, [chat.isOpen, cancelVoice]);

  // A help-nudge tip opened the panel with its question ready (#4601): put it
  // in the input, unsent, with the caret at the end -- the user presses Send.
  const { prefill, clearPrefill } = chat;
  useEffect(() => {
    if (!chat.isOpen || prefill === null) return undefined;
    const text = prefill.slice(0, MAX_MESSAGE_CHARS);
    setDraft(text);
    clearPrefill();
    // No cleanup: clearing the prefill re-runs this effect at once, and the
    // caret move must still happen after the modal has focused the input.
    window.setTimeout(() => {
      const el = inputRef.current;
      el?.focus();
      el?.setSelectionRange?.(text.length, text.length);
    }, 0);
    return undefined;
  }, [chat.isOpen, prefill, clearPrefill]);

  // An empty chat (on open, and after "New chat") opens at the top, so the
  // greeting is in view above the suggested questions (#3684).
  const empty = chat.messages.length === 0;
  useEffect(() => {
    if (!chat.isOpen || !empty || chat.offline) return undefined;
    // After the modal has mounted and moved focus to the input.
    const id = window.setTimeout(
      () => greetingRef.current?.scrollIntoView?.({ block: "start" }),
      0,
    );
    return () => window.clearTimeout(id);
  }, [chat.isOpen, empty, chat.offline]);

  const close = () => chat.setOpen(false);

  const launchTour = (tourId: string) => {
    close();
    // Let the panel unmount before the tour engine measures its first anchor.
    window.setTimeout(() => startTour(tourId), 0);
  };

  const reportProblem = () => {
    close();
    window.setTimeout(() => openFeedback({ type: "bug" }), 0);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || chat.streaming) return;
    setDraft("");
    void chat.send(text, { route: location.pathname, allowedRoutes, allowedTours });
  };

  // A suggested-question pill goes through the same path as typing and sending (#3679).
  const ask = (text: string) => {
    if (chat.streaming) return;
    void chat.send(text, { route: location.pathname, allowedRoutes, allowedTours });
  };

  const lastUserText =
    [...chat.messages].reverse().find((m) => m.role === "user")?.text ?? "";

  const quickActions = (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        onClick={reportProblem}
        className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[var(--color-border)] px-3 text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]"
      >
        <Bug className="h-4 w-4 text-[var(--color-accent)]" aria-hidden="true" />
        Report a problem
      </button>
      {quickTourId && (
        <button
          type="button"
          onClick={() => launchTour(quickTourId)}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-[var(--color-border)] px-3 text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]"
        >
          <GraduationCap className="h-4 w-4 text-[var(--color-accent)]" aria-hidden="true" />
          Take a tour
        </button>
      )}
    </div>
  );

  // "New chat" on a chat that is already new would do nothing, so it says so
  // (aria-disabled + tooltip) rather than looking pressable (#3702). It stays
  // focusable so the tooltip and its accessible name are still reachable.
  const alreadyNew = empty && !chat.streaming;
  const startNewChat = () => {
    // A new chat never inherits a half-made voice note from the old one.
    voice.cancel();
    if (!alreadyNew) chat.newChat();
    // Keep the user in the input either way, ready to type the new question.
    inputRef.current?.focus();
  };
  const newChatButton = (
    <button
      type="button"
      onClick={startNewChat}
      aria-label="Start a new chat"
      aria-disabled={alreadyNew || undefined}
      title={alreadyNew ? "This is already a new chat" : "Start a new chat"}
      data-testid="support-new-chat"
      className={
        "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-[var(--color-text-muted)] transition-colors " +
        (alreadyNew
          ? "cursor-default opacity-40"
          : "hover:bg-[var(--color-bg-page)] hover:text-[var(--color-text-primary)]")
      }
    >
      <SquarePen className="h-5 w-5" aria-hidden="true" />
    </button>
  );

  return (
    <Modal
      open={chat.isOpen}
      onClose={close}
      title={assistantName}
      titleIcon={<BeyondMark size={22} />}
      placement="corner"
      size="md"
      bodyScroll
      feedbackTrigger={false}
      closeLabel="Close help chat"
      headerActions={chat.offline ? undefined : newChatButton}
      initialFocusRef={inputRef}
      zIndexClass="z-[60]"
      className="h-[85vh] sm:h-[70vh] sm:max-h-[640px] sm:w-[400px] sm:border sm:border-[var(--color-border)]"
      dataTour="support-panel"
      footer={
        chat.offline ? (
          <div className="w-full">{quickActions}</div>
        ) : (
          <div className="flex w-full flex-col gap-3">
            {quickActions}
            <VoiceNoteStatus voice={voice} />
            <form onSubmit={submit} className="flex items-center gap-2">
              <label htmlFor="support-chat-input" className="sr-only">
                Ask {assistantName} a question
              </label>
              <input
                id="support-chat-input"
                ref={inputRef}
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={MAX_MESSAGE_CHARS}
                autoComplete="off"
                placeholder="Ask how to do something…"
                className="min-h-[44px] min-w-0 flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-input)] px-3 py-2 text-base text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-accent)] focus:outline-hidden"
              />
              <VoiceNoteButton voice={voice} />
              <button
                type="submit"
                disabled={!draft.trim() || chat.streaming || voice.phase !== "idle"}
                aria-label="Send"
                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
              >
                <Send className="h-5 w-5" />
              </button>
            </form>
          </div>
        )
      }
    >
      <div className="px-5 py-4 sm:px-6">
        {chat.offline ? (
          <OfflineHelp
            reason={chat.offline.reason}
            initialQuery={lastUserText}
            allowedRoutes={allowedRoutes}
            allowedTours={allowedTours}
            onStartTour={launchTour}
            onNavigate={close}
            onRetryChat={chat.offline.reason === "busy" ? chat.retryChat : undefined}
          />
        ) : (
          <>
            {chat.messages.length === 0 && (
              <div
                ref={greetingRef}
                className="mb-3 text-sm text-[var(--color-text-secondary)]"
                data-testid="support-greeting"
              >
                <p className="font-medium text-[var(--color-text-primary)]">
                  Hi, I'm the {assistantName} AI Assistant.
                </p>
                <p className="mt-2">I'm here to help:</p>
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  <li>
                    Ask me how to do something in the CRM, where to find a page, or what a screen
                    is for.
                  </li>
                  <li>I can report a problem for you</li>
                  <li>I can't see or change your records.</li>
                </ul>
              </div>
            )}
            {chat.messages.length === 0 && (
              <SuggestedQuestions
                allowedRoutes={allowedRoutes}
                onAsk={ask}
                disabled={chat.streaming}
              />
            )}
            <SupportMessageList
              messages={chat.messages}
              streaming={chat.streaming}
              awaitingFirstToken={chat.awaitingFirstToken}
              allowedRoutes={allowedRoutes}
              onLinkClick={close}
              onRunAction={chat.runMessageAction}
              assistantName={assistantName}
              currentRoute={location.pathname}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
